import type { OutreachBrandState, OutreachRow } from '@discovr/types';
import { isWithBrandStatus } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { fmtDateTime } from '../../utils/campaignDetailHelpers';
import { brandStatePill } from './brandPill';

export interface BrandStripProps {
  row: OutreachRow;
  canWrite: boolean;
  onOpenRosterChat: () => void;
  onRecordCounter: () => void;
  onClearCounter?: () => void;
}

const SENTENCES: Record<Exclude<OutreachBrandState, 'approved'>, string> = {
  none: 'This creator is with the brand.',
  pending: "Waiting for the brand's decision.",
  counter_requested: 'The brand asked for less. Counter the creator from the reply box, then agree the new rate when they accept.',
  rejected: 'The brand declined. Send the creator a polite close, or close the conversation.',
};

export function BrandStrip({ row, canWrite, onOpenRosterChat, onRecordCounter, onClearCounter }: BrandStripProps) {
  if (!isWithBrandStatus(row.status)) return null;
  const state = row.brandState ?? 'none';
  // Approved reads just "Brand approved": telling the creator is the V2 final confirmation.
  const label = state === 'approved' ? 'Brand approved' : (brandStatePill(row)?.label ?? 'With the brand');
  const decided = row.brandDecidedAt && state !== 'pending' ? ` · decided ${fmtDateTime(row.brandDecidedAt)}` : '';
  const sentence = state === 'approved' ? null : SENTENCES[state];
  // Go answers ENTRY_NOT_WITH_BRAND for a brand-counter outside pending or without a hand-off.
  const canRecordCounter = canWrite && row.status === 'pending' && state === 'pending' && !!row.handedOffAt;

  return (
    <section aria-label="Brand" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-(--stone-50) px-4 py-3">
      <div className="min-w-0">
        <p className="text-body-sm-medium text-foreground">{`${label}${decided}`}</p>
        {sentence && <p className="text-caption-regular text-muted-foreground">{sentence}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onOpenRosterChat}>
          Open roster chat
        </Button>
        {canRecordCounter && (
          <Button type="button" variant="outline" size="sm" onClick={onRecordCounter}>
            Record brand counter…
          </Button>
        )}
        {canWrite && state === 'counter_requested' && onClearCounter && (
          <Button type="button" variant="outline" size="sm" onClick={onClearCounter}>
            Clear
          </Button>
        )}
      </div>
    </section>
  );
}
